import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const LoginAndAuthentication = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const features = [
    { icon: 'Users', label: 'Gestión de clientes' },
    { icon: 'UserCheck', label: 'Gestión de empleados' },
    { icon: 'BarChart3', label: 'Control de loncheados' },
    { icon: 'TrendingUp', label: 'Rentabilidad en tiempo real' },
    { icon: 'BarChart2', label: 'Análisis financiero' },
    { icon: 'Calendar', label: 'Calendario operativo' },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email) newErrors.email = 'El correo electrónico es obligatorio';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) newErrors.email = 'Formato de correo inválido';
    if (!formData?.password) newErrors.password = 'La contraseña es obligatoria';
    else if (formData?.password?.length < 6) newErrors.password = 'Mínimo 6 caracteres';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const { error } = await supabase?.auth?.signInWithPassword({ email: formData?.email, password: formData?.password });
      if (error) throw error;
      navigate('/');
    } catch (err) {
      setErrors(prev => ({ ...prev, general: err?.message || 'Error al iniciar sesión' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Acceso al CRM - Las Dos Doncellas</title>
        <meta name="description" content="Acceso seguro al CRM de Las Dos Doncellas Productos Ibéricos." />
      </Helmet>
      <div
        className="min-h-screen flex flex-col"
        style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 40%, #0D0D0D 70%, #0A0A0A 100%)' }}>

        {/* Main content */}
        <div className="flex-1 flex items-center justify-center p-4 md:p-6 lg:p-8">
          <div className="w-full max-w-6xl">
            <div className="flex flex-col lg:flex-row rounded-2xl overflow-hidden"
              style={{
                border: '1px solid rgba(214,239,78,0.15)',
                boxShadow: '0 25px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)'
              }}>

              {/* ── LEFT PANEL (40%) ── */}
              <div
                className="lg:w-[40%] flex flex-col items-center justify-center p-8 lg:p-12 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(160deg, #111111 0%, #0D0D0D 50%, #0A0A0A 100%)',
                  borderRight: '1px solid rgba(214,239,78,0.12)'
                }}>

                {/* Decorative glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full opacity-5"
                  style={{ background: 'radial-gradient(circle, #D6EF4E 0%, transparent 70%)' }} />

                {/* Logo */}
                <div className="relative z-10 flex flex-col items-center text-center mb-8">
                  <div className="mb-6 p-3 rounded-2xl"
                    style={{ background: 'rgba(214,239,78,0.06)', border: '1px solid rgba(214,239,78,0.15)' }}>
                    <img
                      src="/assets/images/logo_las_dos_doncellas_1MB-1784658867185.jpg"
                      alt="Las Dos Doncellas"
                      className="w-28 h-28 lg:w-36 lg:h-36 object-contain rounded-xl" />
                  </div>

                  <h1 className="font-oranienbaum font-bold text-white text-2xl lg:text-3xl tracking-wide mb-1">
                    LAS DOS DONCELLAS
                  </h1>
                  <p className="font-oranienbaum text-[#D6EF4E] text-sm lg:text-base tracking-widest uppercase mb-4">
                    CRM Profesional
                  </p>
                  <p className="font-oranienbaum text-white/50 text-sm leading-relaxed max-w-xs">
                    Centro de gestión integral para operaciones, clientes, empleados, rentabilidad y control financiero.
                  </p>
                </div>

                {/* Feature list */}
                <div className="relative z-10 w-full max-w-xs space-y-3">
                  {features?.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
                        style={{ background: 'rgba(214,239,78,0.12)', border: '1px solid rgba(214,239,78,0.2)' }}>
                        <Icon name={feature?.icon} size={14} className="text-[#D6EF4E]" />
                      </div>
                      <span className="font-oranienbaum text-white/70 text-sm">{feature?.label}</span>
                    </div>
                  ))}
                </div>

                {/* Bottom decoration */}
                <div className="absolute bottom-0 left-0 right-0 h-px"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(214,239,78,0.3), transparent)' }} />
              </div>

              {/* ── RIGHT PANEL (60%) ── */}
              <div
                className="lg:w-[60%] flex flex-col justify-center p-8 lg:p-12"
                style={{ background: 'linear-gradient(160deg, #111111 0%, #0D0D0D 100%)' }}>

                {/* Card header */}
                <div className="mb-8">
                  <h2 className="font-oranienbaum font-bold text-white text-3xl lg:text-4xl mb-2">
                    Acceso al CRM
                  </h2>
                  <p className="font-oranienbaum text-white/50 text-base">
                    Introduzca sus credenciales para continuar.
                  </p>
                  <div className="mt-4 h-px w-16"
                    style={{ background: 'linear-gradient(90deg, #D6EF4E, transparent)' }} />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5 max-w-md">

                  {/* Email */}
                  <div>
                    <label className="block font-oranienbaum text-white/70 text-sm mb-2">
                      Correo electrónico
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2">
                        <Icon name="Mail" size={18} className="text-white/30" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData?.email}
                        onChange={handleChange}
                        placeholder="correo@lasdosdoncellas.com"
                        disabled={loading}
                        className="w-full pl-10 pr-4 py-3.5 font-oranienbaum text-white text-sm rounded-xl outline-none transition-all"
                        style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: errors?.email ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)',
                          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)'
                        }}
                        onFocus={e => { e.target.style.border = '1px solid rgba(214,239,78,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(214,239,78,0.08)'; }}
                        onBlur={e => { e.target.style.border = errors?.email ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.3)'; }} />
                    </div>
                    {errors?.email && <p className="mt-1.5 font-oranienbaum text-red-400 text-xs">{errors?.email}</p>}
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block font-oranienbaum text-white/70 text-sm mb-2">
                      Contraseña
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2">
                        <Icon name="Lock" size={18} className="text-white/30" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData?.password}
                        onChange={handleChange}
                        placeholder="Introduzca su contraseña"
                        disabled={loading}
                        className="w-full pl-10 pr-12 py-3.5 font-oranienbaum text-white text-sm rounded-xl outline-none transition-all"
                        style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: errors?.password ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)',
                          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)'
                        }}
                        onFocus={e => { e.target.style.border = '1px solid rgba(214,239,78,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(214,239,78,0.08)'; }}
                        onBlur={e => { e.target.style.border = errors?.password ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.3)'; }} />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                        <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                      </button>
                    </div>
                    {errors?.password && <p className="mt-1.5 font-oranienbaum text-red-400 text-xs">{errors?.password}</p>}
                  </div>

                  {/* Remember me */}
                  <div className="flex items-center gap-3">
                    <div
                      onClick={() => !loading && setFormData(prev => ({ ...prev, rememberMe: !prev?.rememberMe }))}
                      className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 cursor-pointer transition-all"
                      style={{
                        background: formData?.rememberMe ? '#D6EF4E' : 'rgba(255,255,255,0.05)',
                        border: formData?.rememberMe ? '1px solid #D6EF4E' : '1px solid rgba(255,255,255,0.2)'
                      }}>
                      {formData?.rememberMe && <Icon name="Check" size={12} className="text-black" />}
                    </div>
                    <span className="font-oranienbaum text-white/60 text-sm cursor-pointer"
                      onClick={() => !loading && setFormData(prev => ({ ...prev, rememberMe: !prev?.rememberMe }))}>
                      Recordarme
                    </span>
                  </div>

                  {/* General error */}
                  {errors?.general && (
                    <div className="flex items-start gap-3 p-4 rounded-xl"
                      style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
                      <Icon name="AlertCircle" size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="font-oranienbaum text-red-400 text-sm">{errors?.general}</p>
                    </div>
                  )}

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 font-oranienbaum font-bold text-black text-base rounded-xl transition-all duration-200 flex items-center justify-center gap-3 mt-2"
                    style={{
                      background: loading ? 'rgba(214,239,78,0.5)' : 'linear-gradient(135deg, #D6EF4E 0%, #c8e040 100%)',
                      boxShadow: loading ? 'none' : '0 4px 20px rgba(214,239,78,0.25)',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={e => { if (!loading) { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 6px 28px rgba(214,239,78,0.35)'; }}}
                    onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 20px rgba(214,239,78,0.25)'; }}>
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Iniciando sesión...
                      </>
                    ) : (
                      <>
                        <Icon name="LogIn" size={20} />
                        INICIAR SESIÓN
                      </>
                    )}
                  </button>

                  {/* Security indicators */}
                  <div className="flex flex-wrap gap-3 pt-2">
                    {[
                      { icon: 'Lock', label: 'Conexión segura' },
                      { icon: 'Shield', label: 'Datos protegidos' },
                      { icon: 'CheckCircle', label: 'Acceso autorizado' },
                    ]?.map((item, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <Icon name={item?.icon} size={13} className="text-[#D6EF4E]/60" />
                        <span className="font-oranienbaum text-white/35 text-xs">{item?.label}</span>
                      </div>
                    ))}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-5 text-center"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="font-oranienbaum text-white/25 text-xs">
            Las Dos Doncellas Productos Ibéricos · Castilblanco de los Arroyos · {new Date()?.getFullYear()}
          </p>
        </footer>
      </div>
    </>
  );
};

export default LoginAndAuthentication;